import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, Paper, Stack, Typography,
} from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';

import { api, ApiError } from '../api/client';
import type { PlanRequest, RoutePlan } from '../api/types';

/** One way round, and what it was told not to repeat. */
interface Variant {
  plan: RoutePlan;
  /**
   * The request that produced it, handed back when it is adopted.
   *
   * Without it the page adopts a plan whose request it does not have, its
   * own draft then differs from what it last sent, and the live re-plan
   * quietly replaces the option that was just chosen — which looked like
   * the options list clearing itself.
   */
  request: PlanRequest;
  /** Everything the options before it used, which is what made it different. */
  excluded: string[];
  /** What this one visits, so the next option can be told to avoid it. */
  stopIds: string[];
}

function stopIdsOf(plan: RoutePlan): string[] {
  return plan.stops
    .filter((stop) => stop.kind === 'visit' && stop.poiId)
    .map((stop) => stop.poiId as string);
}

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return hours > 0 ? `${hours} hr${rest ? ` ${rest} min` : ''}` : `${rest} min`;
}

/** How many are built each time somebody asks for more. */
const BATCH = 2;

/**
 * The tasking, without the exclusions.
 *
 * Adopting an option puts its exclusions on the form, which changes the
 * request — and if that counted as a new tasking, the options would clear
 * themselves the moment one was chosen, taking the way back with them. What
 * makes a tasking different is where it goes, when, how and under what
 * emphasis; which places it was told to skip is how the options differ from
 * each other.
 */
function taskingSignature(request: PlanRequest): string {
  const { excludePoiIds, ...rest } = request;
  return JSON.stringify(rest);
}

/**
 * Other ways to run the same tasking, sharing no stops.
 *
 * An emphasis says what a round is FOR; it does not say that there is only
 * one round that answers it. Washington DC holds more police stations than a
 * day can visit, and an operator comparing options wants genuinely different
 * days — not the same seven places in a different order.
 *
 * So each option is planned with every earlier option's stops excluded. That
 * is the same mechanism a multi-day operation uses to stop day four repeating
 * day one, pointed at one day instead of a week.
 *
 * Two at a time, because each one is a full pipeline run — the matrix, the
 * solve, the routing and the provider lookups behind them. Somebody who wants
 * more can ask for more.
 */
export function RouteVariants({
  request,
  plan,
  onApply,
  appliedIndex = null,
}: {
  /** The request that produced the plan on screen. */
  request: PlanRequest | null;
  /** The plan on screen, whose stops the first option must avoid. */
  plan: RoutePlan | null;
  /**
   * Adopt one.
   *
   * The exclusions travel with it: without them the form's next re-plan would
   * return the option that was replaced, and choosing would look undone.
   */
  onApply: (
    plan: RoutePlan,
    excludePoiIds: string[],
    index: number,
    request: PlanRequest,
  ) => void;
  /** Which option is drawn on the map, so the list can say so. */
  appliedIndex?: number | null;
}) {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  /**
   * The route this started from, kept so it can be gone back to.
   *
   * Without it, adopting an option was a one-way door: the original was on
   * nobody's list and the only way back was to plan the day again.
   */
  const [original, setOriginal] = useState<Variant | null>(null);

  /*
   * Cleared when the TASKING changes — not when the exclusions do.
   *
   * Options built for a different start, mode or time budget are not options
   * for what is on screen now. Options built for the same journey, minus the
   * places one of them used, are exactly what this list is.
   */
  const signature = request ? taskingSignature(request) : null;
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (signature === lastSignature.current) return;
    lastSignature.current = signature;
    setVariants([]);
    setOriginal(null);
    setError(null);
    setExhausted(false);
  }, [signature]);

  /* The first plan seen for this tasking is the one to come back to. */
  useEffect(() => {
    if (original || !plan || !request) return;
    setOriginal({
      plan,
      request,
      excluded: request.excludePoiIds ?? [],
      stopIds: stopIdsOf(plan),
    });
  }, [plan, request, original]);

  if (!request || !plan) return null;

  const load = async (count: number) => {
    setLoading(true);
    setError(null);
    try {
      const built = [...variants];
      /*
       * Sequential, not parallel: option three has to know what option two
       * used, and it cannot know that until option two exists.
       */
      for (let i = 0; i < count; i += 1) {
        const excluded = [
          ...new Set([
            ...(request.excludePoiIds ?? []),
            ...stopIdsOf(plan),
            ...built.flatMap((variant) => variant.stopIds),
          ]),
        ];
        const attempt = { ...request, excludePoiIds: excluded };
        const next = await api.planLatest(attempt);
        const stopIds = stopIdsOf(next);
        /*
         * Nothing left to differ by.
         *
         * When a corridor runs out of places, the planner returns the ends
         * and nothing between them — an "option" that is not one. Said once,
         * rather than offering an empty card each time somebody asks.
         */
        if (stopIds.length === 0) {
          setExhausted(true);
          break;
        }
        built.push({ plan: next, request: attempt, excluded, stopIds });
        setVariants([...built]);
      }
    } catch (err) {
      const abandoned = err instanceof DOMException && err.name === 'AbortError';
      if (!abandoned) setError((err as ApiError).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
        <AltRouteIcon fontSize="small" color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Other ways to run this</Typography>
        {variants.length > 0 && (
          <Chip size="small" variant="outlined" label={`${variants.length} built`} />
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The same tasking under the same emphasis, visiting different sites —
        each option is planned without the stops the ones before it used.
      </Typography>

      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {variants.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {[...(original ? [original] : []), ...variants].map((variant, position) => {
            /*
             * The original is option one and is not one of the built ones, so
             * it carries index -1 — which is also what the page uses to say
             * "back to the route we started from".
             */
            const index = original ? position - 1 : position;
            const isOriginal = index < 0;
            const applied = isOriginal
              ? appliedIndex === null || appliedIndex === -1
              : appliedIndex === index;
            const stops = variant.stopIds.length;
            return (
              <Paper
                key={isOriginal ? 'original' : index}
                variant="outlined"
                sx={{
                  p: 1.5,
                  ...(applied && {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                    bgcolor: 'action.selected',
                  }),
                }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                    {isOriginal ? 'Option 1 — as first planned' : `Option ${index + 2}`}
                  </Typography>
                  {applied && <Chip size="small" color="primary" label="On the map" />}
                  {variant.excluded.length > 0 && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`avoids ${variant.excluded.length} used`}
                    />
                  )}
                </Stack>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {variant.plan.stops
                    .filter((stop) => stop.kind === 'visit')
                    .map((stop) => stop.name)
                    .slice(0, 3)
                    .join(' · ')}
                  {stops > 3 ? ` · and ${stops - 3} more` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stops} stops · {formatDuration(variant.plan.totals.totalMinutes)} ·{' '}
                  {(variant.plan.totals.distanceMeters / 1000).toFixed(1)} km
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant={applied ? 'contained' : 'outlined'}
                    disabled={applied}
                    onClick={() => onApply(variant.plan, variant.excluded, index, variant.request)}
                  >
                    {applied
                      ? 'Showing this route'
                      : isOriginal ? 'Back to this route' : 'Use this route'}
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}

      {loading && <LinearProgress sx={{ mb: 1.5, height: 4, borderRadius: 2 }} />}

      {exhausted && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Nothing else along this corridor to build another option from.
        </Typography>
      )}

      {!exhausted && (
        <Button
          size="small"
          variant={variants.length === 0 ? 'contained' : 'text'}
          disabled={loading}
          onClick={() => void load(BATCH)}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {loading
            ? 'Building…'
            : variants.length === 0
              ? `Show ${BATCH} other ways`
              : `Show ${BATCH} more`}
        </Button>
      )}
    </Paper>
  );
}
